import { x25519 } from '@noble/curves/ed25519.js';
import type {
  StorefrontBuyerOrderCiphertext,
  StorefrontBuyerOrderDecisionPayloadV1,
  StorefrontBuyerOrderStatusPayloadV1,
  StorefrontOrderIntakeKey,
  StorefrontOrderPayloadV1,
  StorefrontOrderRecord,
  StorefrontOrderSubmitRequest,
} from '@stokmerter/contracts';

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/u;
const MAX_QUANTITY = 1_000_000;
const MAX_PRICE_MINOR = 100_000_000_000;

function concatenate(...values: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const result = new Uint8Array(values.reduce((total, value) => total + value.byteLength, 0));
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.byteLength;
  }
  return result;
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const binary = atob(`${normalized}${'='.repeat((4 - normalized.length % 4) % 4)}`);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function toBase64Url(bytes: Uint8Array): string {
  return toBase64(bytes).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

function randomId(prefix: 'sfo' | 'mut'): string {
  return `${prefix}_${toBase64Url(crypto.getRandomValues(new Uint8Array(18)))}`;
}

function orderAad(storefrontId: string, orderId: string, keyVersion: number) {
  return encoder.encode(`stoksypro:storefront-order:v1:${storefrontId}:${orderId}:${keyVersion}`);
}

function statusAad(order: StorefrontOrderRecord, keyVersion: number) {
  return encoder.encode(
    `stoksypro:storefront-order-status:v1:${order.storefrontId}:${order.buyerId}:` +
    `${order.orderId}:${order.status}:${keyVersion}`,
  );
}

function buyerOrderAad(input: {
  storefrontId: string;
  buyerId: string;
  orderId: string;
  keyVersion: number;
}) {
  return encoder.encode(
    `stoksypro:storefront-buyer-order:v1:${input.storefrontId}:${input.buyerId}:` +
    `${input.orderId}:${input.keyVersion}`,
  );
}

function buyerDecisionAad(input: {
  storefrontId: string;
  buyerId: string;
  orderId: string;
  keyVersion: number;
}) {
  return encoder.encode(
    `stoksypro:storefront-buyer-decision:v1:${input.storefrontId}:${input.buyerId}:` +
    `${input.orderId}:${input.keyVersion}`,
  );
}

function validText(value: unknown, maxLength: number, allowEmpty = false): value is string {
  return typeof value === 'string' && value.length <= maxLength &&
    !CONTROL_CHARACTERS.test(value) && value === value.normalize('NFKC').trim() &&
    (allowEmpty || value.length > 0);
}

function validQuantity(value: unknown, allowZero = true): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) &&
    value >= (allowZero ? 0 : 1) && value <= MAX_QUANTITY;
}

function validPrice(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isSafeInteger(value) &&
    value >= 0 && value <= MAX_PRICE_MINOR);
}

function validPayload(value: unknown): value is StorefrontOrderPayloadV1 {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const payload = value as Partial<StorefrontOrderPayloadV1>;
  if (payload.version !== 1 || !Array.isArray(payload.items) || payload.items.length < 1 ||
    payload.items.length > 100 || !validText(payload.shippingAddress, 4_000) ||
    !validText(payload.submittedAt, 64) || !Number.isFinite(Date.parse(payload.submittedAt)) ||
    !(payload.note === null || validText(payload.note, 2_000))) return false;
  const identities = new Set<string>();
  return payload.items.every((line) => {
    if (!line || typeof line !== 'object') return false;
    const identity = `${line.productId}:${line.variantId}`;
    if (identities.has(identity)) return false;
    identities.add(identity);
    return validText(line.productId, 160) && line.productId.startsWith('prd_') &&
      validText(line.variantId, 160) && line.variantId.startsWith('sfv_') &&
      validText(line.productCode, 160) && validText(line.productName, 300) &&
      validText(line.colorName, 160) && validQuantity(line.seriesCount, false) &&
      validQuantity(line.seriesQuantity) && validQuantity(line.extraQuantity) &&
      line.seriesQuantity + line.extraQuantity > 0 && validPrice(line.displayedPriceTlMinor) &&
      validPrice(line.displayedPriceUsdMinor) && validQuantity(line.catalogRecordVersion, false);
  });
}

function payloadMatchesManifest(payload: StorefrontOrderPayloadV1, order: StorefrontOrderRecord) {
  if (payload.items.length !== order.lineManifest.length) return false;
  const manifest = new Map(order.lineManifest.map((line) => [
    `${line.productId}:${line.variantId}`,
    line.catalogRecordVersion,
  ]));
  return payload.items.every((line) => manifest.get(`${line.productId}:${line.variantId}`) ===
    line.catalogRecordVersion);
}

export async function sealStorefrontOrder(input: {
  buyerId: string;
  catalogKey: CryptoKey;
  catalogKeyVersion: number;
  storefrontId: string;
  catalogRevision: number;
  intakeKey: StorefrontOrderIntakeKey;
  payload: StorefrontOrderPayloadV1;
}): Promise<StorefrontOrderSubmitRequest> {
  const orderId = randomId('sfo');
  const ephemeralPrivateKey = x25519.utils.randomSecretKey();
  const ephemeralPublicKey = x25519.getPublicKey(ephemeralPrivateKey);
  const sharedSecret = x25519.getSharedSecret(
    ephemeralPrivateKey,
    fromBase64(input.intakeKey.publicKey),
  );
  const prefix = encoder.encode(
    `stoksypro:storefront-order-wrap:v1:${input.storefrontId}:${orderId}:${input.intakeKey.keyVersion}:`,
  );
  const rawKey = await crypto.subtle.digest('SHA-256', concatenate(prefix, sharedSecret));
  const key = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt']);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({
    name: 'AES-GCM',
    iv: nonce,
    additionalData: orderAad(input.storefrontId, orderId, input.intakeKey.keyVersion),
    tagLength: 128,
  }, key, encoder.encode(JSON.stringify(input.payload)));
  const buyerNonce = crypto.getRandomValues(new Uint8Array(12));
  const buyerCiphertext = await crypto.subtle.encrypt({
    name: 'AES-GCM',
    iv: buyerNonce,
    additionalData: buyerOrderAad({
      storefrontId: input.storefrontId,
      buyerId: input.buyerId,
      orderId,
      keyVersion: input.catalogKeyVersion,
    }),
    tagLength: 128,
  }, input.catalogKey, encoder.encode(JSON.stringify(input.payload)));
  return {
    orderId,
    catalogRevision: input.catalogRevision,
    lineManifest: input.payload.items.map((item) => ({
      productId: item.productId,
      variantId: item.variantId,
      catalogRecordVersion: item.catalogRecordVersion,
    })),
    encryptedOrder: {
      format: 'x25519-aes256gcm-v1',
      keyVersion: input.intakeKey.keyVersion,
      ephemeralPublicKey: toBase64Url(ephemeralPublicKey),
      nonce: toBase64(nonce),
      ciphertext: toBase64(new Uint8Array(ciphertext)),
    },
    encryptedBuyerOrder: {
      format: 'aes256gcm-storefront-buyer-order-v1',
      keyVersion: input.catalogKeyVersion,
      nonce: toBase64(buyerNonce),
      ciphertext: toBase64(new Uint8Array(buyerCiphertext)),
    },
    mutationId: randomId('mut'),
  };
}

export async function decryptBuyerOrder(input: {
  catalogKey: CryptoKey;
  order: StorefrontOrderRecord;
}): Promise<StorefrontOrderPayloadV1 | null> {
  const sealed: StorefrontBuyerOrderCiphertext | null = input.order.encryptedBuyerOrder;
  if (!sealed) return null;
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: fromBase64(sealed.nonce),
    additionalData: buyerOrderAad({
      storefrontId: input.order.storefrontId,
      buyerId: input.order.buyerId,
      orderId: input.order.orderId,
      keyVersion: sealed.keyVersion,
    }),
    tagLength: 128,
  }, input.catalogKey, fromBase64(sealed.ciphertext));
  const payload = JSON.parse(decoder.decode(plaintext)) as unknown;
  if (!validPayload(payload) || !payloadMatchesManifest(payload, input.order)) {
    throw new Error('INVALID_STOREFRONT_BUYER_ORDER');
  }
  return payload;
}

export async function decryptBuyerOrderStatus(input: {
  catalogKey: CryptoKey;
  order: StorefrontOrderRecord;
}): Promise<StorefrontBuyerOrderStatusPayloadV1 | null> {
  const sealed = input.order.encryptedBuyerStatus;
  if (!sealed) return null;
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: fromBase64(sealed.nonce),
    additionalData: statusAad(input.order, sealed.keyVersion),
    tagLength: 128,
  }, input.catalogKey, fromBase64(sealed.ciphertext));
  const parsed = JSON.parse(decoder.decode(plaintext)) as StorefrontBuyerOrderStatusPayloadV1;
  if (parsed.version !== 1 || parsed.status !== input.order.status) {
    throw new Error('INVALID_STOREFRONT_ORDER_STATUS');
  }
  return parsed;
}

export async function decryptBuyerOrderDecision(input: {
  catalogKey: CryptoKey;
  order: StorefrontOrderRecord;
}): Promise<StorefrontBuyerOrderDecisionPayloadV1 | null> {
  const sealed = input.order.encryptedBuyerDecision;
  if (!sealed) return null;
  const plaintext = await crypto.subtle.decrypt({
    name: 'AES-GCM',
    iv: fromBase64(sealed.nonce),
    additionalData: buyerDecisionAad({
      storefrontId: input.order.storefrontId,
      buyerId: input.order.buyerId,
      orderId: input.order.orderId,
      keyVersion: sealed.keyVersion,
    }),
    tagLength: 128,
  }, input.catalogKey, fromBase64(sealed.ciphertext));
  const parsed = JSON.parse(decoder.decode(plaintext)) as StorefrontBuyerOrderDecisionPayloadV1;
  const manifest = new Set(input.order.lineManifest.map((line) =>
    `${line.productId}:${line.variantId}`));
  const identities = new Set<string>();
  if (parsed.version !== 1 || !['approved', 'rejected'].includes(parsed.outcome) ||
    !Array.isArray(parsed.lines) || parsed.lines.length > 100 ||
    (parsed.outcome === 'approved' && parsed.lines.length !== manifest.size) ||
    (parsed.outcome === 'rejected' && parsed.lines.length !== 0) ||
    !validText(parsed.decidedAt, 64) || !Number.isFinite(Date.parse(parsed.decidedAt)) ||
    !(parsed.message === null || validText(parsed.message, 500, true)) ||
    !parsed.lines.every((line) => {
      const identity = `${line.productId}:${line.variantId}`;
      if (!manifest.has(identity) || identities.has(identity)) return false;
      identities.add(identity);
      return validQuantity(line.approvedSeriesQuantity) &&
        validQuantity(line.approvedExtraQuantity);
    })) {
    throw new Error('INVALID_STOREFRONT_ORDER_DECISION');
  }
  return parsed;
}
